import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { recordGameScore, getCurrentUser, calculateScore, determineClassLevel } from '../utils/gameUtils';

const NumberShooting = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [question, setQuestion] = useState(null);
  const [targets, setTargets] = useState([]);
  const [explosions, setExplosions] = useState([]);
  const [combo, setCombo] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameOver, setGameOver] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [scoreRecorded, setScoreRecorded] = useState(false);

  const generateQuestion = () => {
    const num1 = Math.floor(Math.random() * 5000) + 1000;
    const num2 = Math.floor(Math.random() * 3000) + 500;
    const operation = Math.random() > 0.5 ? '+' : '-';
    const answer = operation === '+' ? num1 + num2 : num1 - num2;
    
    const wrongAnswers = [
      answer + Math.floor(Math.random() * 200) + 50,
      answer - Math.floor(Math.random() * 200) - 50,
      answer + Math.floor(Math.random() * 100) + 10,
      answer - Math.floor(Math.random() * 150) - 25
    ];
    
    const allTargets = [answer, ...wrongAnswers]
      .sort(() => Math.random() - 0.5)
      .slice(0, 6)
      .map((value, idx) => ({
        id: Date.now() + idx,
        value,
        isCorrect: value === answer,
        x: 10 + (idx % 3) * 30,
        y: 20 + Math.floor(idx / 3) * 40,
        scale: 1,
        hit: false
      }));
    
    setQuestion({ num1, num2, operation, answer });
    setTargets(allTargets);
  };

  useEffect(() => {
    generateQuestion();
  }, []);

  // Record score when game ends
  useEffect(() => {
    if (gameOver && !scoreRecorded && totalQuestions > 0) {
      const recordScore = async () => {
        try {
          const user = getCurrentUser();
          if (user) {
            const finalScore = calculateScore(correctAnswers, totalQuestions);
            const classLevel = determineClassLevel('NumberShooting');
            
            await recordGameScore({
              userId: user.id,
              class: classLevel,
              score: finalScore,
              correct: correctAnswers,
              total: totalQuestions
            });
            
            setScoreRecorded(true);
            console.log(`🎉 Score recorded: ${correctAnswers}/${totalQuestions} (${finalScore}%)`);
          }
        } catch (error) {
          console.error('Failed to record score:', error);
        }
      };
      
      recordScore();
    }
  }, [gameOver, scoreRecorded, totalQuestions, correctAnswers]);

  useEffect(() => {
    if (timeLeft > 0 && !gameOver) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setGameOver(true);
    }
  }, [timeLeft, gameOver]);

  useEffect(() => {
    // Animate targets
    const animation = setInterval(() => {
      setTargets(prev => prev.map(t => ({
        ...t,
        scale: t.hit ? t.scale - 0.1 : 1 + Math.sin(Date.now() / 200) * 0.1
      })).filter(t => t.scale > 0));
    }, 50);

    return () => clearInterval(animation);
  }, []);

  const handleShoot = (target) => {
    if (target.hit) return;

    // Track total questions answered
    setTotalQuestions(prev => prev + 1);

    if (target.isCorrect) {
      const points = 10 * (combo + 1);
      setScore(score + points);
      setCombo(combo + 1);
      setCorrectAnswers(prev => prev + 1);
      
      // Create explosion effect
      setExplosions(prev => [...prev, { id: Date.now(), x: target.x, y: target.y }]);
      setTimeout(() => {
        setExplosions(prev => prev.filter(e => e.id !== Date.now()));
      }, 500);
      
      // Mark as hit and generate new question
      setTargets(prev => prev.map(t => 
        t.id === target.id ? { ...t, hit: true } : t
      ));
      
      setTimeout(generateQuestion, 800);
    } else {
      setCombo(0);
      setTargets(prev => prev.map(t => 
        t.id === target.id ? { ...t, hit: true } : t
      ));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-800 to-purple-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 mb-4 text-white">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/games')} className="hover:text-yellow-300">
              ← Back
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold">🎯 Number Shooting</h1>
              {question && (
                <div className="text-4xl font-bold mt-2">
                  {question.num1} {question.operation} {question.num2} = ?
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm">Combo: x{combo + 1}</div>
              <div className="text-2xl font-bold">{score}</div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">⏱️</div>
            <div className="text-xl font-bold">{timeLeft}s</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">🔥</div>
            <div className="text-xl font-bold">x{combo + 1}</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">⭐</div>
            <div className="text-xl font-bold">{score}</div>
          </div>
        </div>

        {!gameOver ? (
          /* Game Area */
          <div className="relative bg-black/40 rounded-xl p-8" style={{ height: '600px' }}>
            {/* Crosshair */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className="w-8 h-8 border-2 border-red-500 rounded-full opacity-50" />
            </div>

            {/* Targets */}
            {targets.map(target => (
              <button
                key={target.id}
                onClick={() => handleShoot(target)}
                disabled={target.hit}
                className={`absolute transition-all duration-200 ${
                  target.isCorrect 
                    ? 'bg-gradient-to-br from-green-400 to-green-600' 
                    : 'bg-gradient-to-br from-red-400 to-red-600'
                } text-white font-bold rounded-full shadow-2xl hover:shadow-3xl disabled:opacity-0`}
                style={{
                  left: `${target.x}%`,
                  top: `${target.y}%`,
                  width: '120px',
                  height: '120px',
                  transform: `translate(-50%, -50%) scale(${target.scale})`,
                  fontSize: '24px'
                }}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="text-3xl mb-1">🎯</div>
                  <div>{target.value}</div>
                </div>
              </button>
            ))}

            {/* Explosions */}
            {explosions.map(exp => (
              <div
                key={exp.id}
                className="absolute text-6xl animate-ping"
                style={{
                  left: `${exp.x}%`,
                  top: `${exp.y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                💥
              </div>
            ))}

            {/* Instructions */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-center bg-black/50 p-3 rounded-lg">
              <p className="font-bold mb-1">Click the correct answer!</p>
              <p className="text-sm">Green = Correct | Red = Wrong</p>
            </div>
          </div>
        ) : (
          /* Game Over */
          <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Game Over!</h2>
            <div className="text-xl text-gray-600 mb-6">
              <p>Questions Answered: <span className="font-bold text-blue-600">{totalQuestions}</span></p>
              <p>Correct Answers: <span className="font-bold text-blue-600">{correctAnswers}</span></p>
              <p>Accuracy: <span className="font-bold text-blue-600">{totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0}%</span></p>
              <p>Best Combo: <span className="font-bold text-blue-600">x{combo + 1}</span></p>
              {scoreRecorded && (
                <p className="text-green-600 font-bold mt-2">✅ Score Recorded!</p>
              )}
            </div>
            <div className="space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700"
              >
                Play Again
              </button>
              <button
                onClick={() => navigate('/games')}
                className="bg-gray-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-700"
              >
                Back to Games
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberShooting;